import React, { useCallback, useRef, useState } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import useMapsStore, { usePrintEventStore } from '../store/useMapsStore';
import { staticMapApiKey, mapId } from '../util/config';
import { GOOGLE_MAP_LIBRARIES, MAP_DEFAULT_ZOOM, POLYGON_DEFAULT_ZOOM } from '../util/constants';
import settings from '../util/config';
import { getUTCHtmlId } from 'common/util/time';
import { lotsStore } from '../store/useLotsStore';
import usePolygonEventStore from '../store/usePolygonEventStore';
import usePolygonsStore from '../store/usePolygonsStore';
import LotStatusFilters from './LotStatusFilters';
import { printMap } from '../util/api-helpers';
import useFiltersStore from '../store/useFiltersStore';
const Map = ({ center, isMobile = false, className, printOnLoad = false, removeControls = false, children }) => {
    const { zoom, setZoom } = useMapsStore();
    const { setHoveredPolygonIndex, setClickedPolygonIndex, setClickedLotId, setHoveredLotId } = usePolygonEventStore(["setHoveredPolygonIndex", "setClickedPolygonIndex", "setClickedLotId", "setHoveredLotId"]);
    const { community } = usePolygonsStore(["community"]);
    const { setIsPrintButtonLoading } = usePrintEventStore();
    const { isLoaded, loadError } = useJsApiLoader({
        id: 'google-maps-script',
        googleMapsApiKey: staticMapApiKey ? staticMapApiKey : '',
        libraries: GOOGLE_MAP_LIBRARIES,
        version: "3.59"
    });
    const mapContainerRef = useRef(null);
    const [mapInstance, setMapInstance] = useState(null);
    const mapHeadingId = 'map-heading-' + getUTCHtmlId();
    const createMapCenterButton = (map) => {
        const controlButtonDiv = document.createElement('div');
        const controlButton = document.createElement('button');
        controlButtonDiv.className = 'g-center-btn';
        controlButton.title = 'Center map';
        controlButton.type = 'button';
        controlButton.addEventListener('click', () => {
            map.setCenter(center);
            if (community?.Properties?.Zoom) {
                map.setZoom(community?.Properties?.Zoom);
            }
            else {
                map.setZoom(POLYGON_DEFAULT_ZOOM);
            }
        });
        controlButtonDiv.appendChild(controlButton);
        return controlButtonDiv;
    };
    const createCompass = (map) => {
        const controlDiv = document.createElement('div');
        controlDiv.className = 'viewport-map__compass';
        const controlImg = document.createElement('img');
        controlImg.setAttribute("aria-label", "Compass");
        controlImg.setAttribute("src", settings.CompassIcon);
        controlDiv.appendChild(controlImg);
        return controlDiv;
    };
    const addControls = (mapInstance) => {
        if (!removeControls) {
            mapInstance.controls[google.maps.ControlPosition.TOP_RIGHT].push(createMapCenterButton(mapInstance));
            if (settings.CompassIcon) {
                mapInstance.controls[google.maps.ControlPosition.TOP_RIGHT].push(createCompass(mapInstance));
            }
        }
    };
    const onLoad = useCallback(function callback(mapInstance) {
        setMapInstance(mapInstance);
        addControls(mapInstance);
    }, []);
    const onUnmount = useCallback(function callback(mapInstance) {
        setMapInstance(null);
    }, []);
    function handleZoomChange() {
        if (mapInstance !== null) {
            const curZoom = mapInstance.getZoom() ?? MAP_DEFAULT_ZOOM;
            setZoom(curZoom);
        }
    }
    function handleDragMap() {
        if (mapInstance !== null) {
            const mapBounds = mapInstance.getBounds();
            if (!mapBounds) {
                return;
            }
        }
    }
    async function handleTilesLoaded() {
        // Once the tiles are loaded, and if it's specified to print, print the map
        if (printOnLoad && mapContainerRef.current && community) {
            try {
                await printMap(mapContainerRef.current, community.Properties, lotsStore.getState().filteredLots.map((lot) => lot.Properties), useFiltersStore.getState().lotStatuses, useFiltersStore.getState().getFiltersToStringArray());
                setIsPrintButtonLoading(false);
            }
            catch (err) {
                console.error('Error with printing the map: ', err);
                setIsPrintButtonLoading(false);
            }
        }
    }
    function onMapClick(event) {
        // close all info windows
        setClickedPolygonIndex(-1);
        setHoveredPolygonIndex(-1);
        setClickedLotId(undefined);
        setHoveredLotId(undefined);
    }
    if (loadError) {
        console.error('google map API load error: ', loadError);
        return React.createElement("p", null, "Error loading the google map API...");
    }
    return isLoaded ? (React.createElement("section", { className: 'viewport-map__map-container' + (className ? ' ' + className : ''), "aria-labelledby": mapHeadingId, ref: mapContainerRef },
        React.createElement("h2", { id: mapHeadingId, className: "sr-only" }, settings.MapSectionLabel),
        React.createElement(GoogleMap, { center: center, onLoad: onLoad, onUnmount: onUnmount, tilt: 0, options: {
                mapId: mapId,
                mapTypeControl: !removeControls,
                streetViewControl: false,
                mapTypeControlOptions: {
                    mapTypeIds: [
                        google.maps.MapTypeId.ROADMAP,
                        google.maps.MapTypeId.HYBRID
                    ],
                    position: google.maps.ControlPosition.TOP_LEFT,
                    style: google.maps.MapTypeControlStyle.DEFAULT
                },
                disableDefaultUI: removeControls,
                keyboardShortcuts: !removeControls,
                tilt: 0,
                rotateControl: false
            }, mapContainerClassName: "viewport-map__map", onClick: onMapClick, onZoomChanged: handleZoomChange, onDragEnd: handleDragMap, onTilesLoaded: handleTilesLoaded, zoom: zoom }, mapInstance != null && children),
        isMobile && React.createElement(LotStatusFilters, null))) : (React.createElement(React.Fragment, null));
};
export default React.memo(Map);
